import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw, Plus, Trash2, Save, RotateCcw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import AdminNav from "@/components/AdminNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";

export default function AdminPricing() {
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/pricing");
      setCfg(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("aero_token");
        navigate("/login");
      } else {
        toast.error("Failed to load pricing");
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const updateService = (i, field, value) => {
    const services = [...cfg.services];
    services[i] = { ...services[i], [field]: field === "name" || field === "key" ? value : Number(value) };
    setCfg({ ...cfg, services });
  };
  const updateCrop = (i, field, value) => {
    const crops = [...cfg.crops];
    crops[i] = { ...crops[i], [field]: field === "name" || field === "key" ? value : Number(value) };
    setCfg({ ...cfg, crops });
  };
  const addService = () => setCfg({ ...cfg, services: [...cfg.services, { key: `service_${cfg.services.length + 1}`, name: "New Service", base_per_acre: 15, min_charge: 200 }] });
  const removeService = (i) => setCfg({ ...cfg, services: cfg.services.filter((_, idx) => idx !== i) });
  const addCrop = () => setCfg({ ...cfg, crops: [...cfg.crops, { key: `crop_${cfg.crops.length + 1}`, name: "New Crop", multiplier: 1.0 }] });
  const removeCrop = (i) => setCfg({ ...cfg, crops: cfg.crops.filter((_, idx) => idx !== i) });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/pricing", cfg);
      setCfg(data);
      toast.success("Pricing updated — live on the website now.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Reset all pricing to defaults? This cannot be undone.")) return;
    try {
      const { data } = await api.post("/admin/pricing/reset");
      setCfg(data);
      toast.success("Pricing reset to defaults");
    } catch {
      toast.error("Reset failed");
    }
  };

  if (loading || !cfg) {
    return (
      <div className="min-h-screen bg-muted/30">
        <AdminNav />
        <div className="flex items-center justify-center py-32 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading pricing…
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-pricing-page" className="min-h-screen bg-muted/30">
      <AdminNav />
      <main className="max-w-7xl mx-auto px-5 md:px-8 py-8 md:py-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display text-3xl md:text-4xl text-primary font-bold">Pricing editor</h1>
            <p className="text-muted-foreground mt-1">Change service rates, crop multipliers and discount rules — updates are instant on the public estimator.</p>
            {cfg.updated_at && (
              <p className="text-xs text-muted-foreground mt-2">Last updated: {new Date(cfg.updated_at).toLocaleString()}</p>
            )}
          </div>
          <div className="flex gap-2">
            <Button onClick={load} variant="outline" size="sm" data-testid="reload-btn" className="rounded-full">
              <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Reload
            </Button>
            <Button onClick={reset} variant="outline" size="sm" data-testid="reset-btn" className="rounded-full">
              <RotateCcw className="h-4 w-4 mr-1.5" /> Reset
            </Button>
            <Button onClick={save} size="sm" disabled={saving} data-testid="save-btn" className="rounded-full bg-primary hover:bg-secondary text-primary-foreground">
              {saving ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Saving…</> : <><Save className="h-4 w-4 mr-1.5" /> Save changes</>}
            </Button>
          </div>
        </div>

        {/* Global params */}
        <section className="bg-card border border-border rounded-3xl shadow-card p-6 md:p-8 mb-8">
          <h2 className="font-display text-xl font-bold text-primary">Discount & adjustments</h2>
          <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-5">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-primary/70">Volume threshold (acres)</Label>
              <Input type="number" min="0" data-testid="vol-threshold" className="mt-2 h-11 rounded-xl bg-background"
                value={cfg.volume_discount_threshold_acres}
                onChange={(e) => setCfg({ ...cfg, volume_discount_threshold_acres: Number(e.target.value) })}
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-primary/70">Volume discount %</Label>
              <Input type="number" min="0" max="80" data-testid="vol-pct" className="mt-2 h-11 rounded-xl bg-background"
                value={cfg.volume_discount_pct}
                onChange={(e) => setCfg({ ...cfg, volume_discount_pct: Number(e.target.value) })}
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-primary/70">Rush fee %</Label>
              <Input type="number" min="0" max="200" data-testid="rush-pct" className="mt-2 h-11 rounded-xl bg-background"
                value={cfg.rush_fee_pct}
                onChange={(e) => setCfg({ ...cfg, rush_fee_pct: Number(e.target.value) })}
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-primary/70">Estimate range ± %</Label>
              <Input type="number" min="0" max="50" data-testid="spread-pct" className="mt-2 h-11 rounded-xl bg-background"
                value={cfg.range_spread_pct}
                onChange={(e) => setCfg({ ...cfg, range_spread_pct: Number(e.target.value) })}
              />
            </div>
          </div>
        </section>

        {/* Services */}
        <section className="bg-card border border-border rounded-3xl shadow-card p-6 md:p-8 mb-8">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="font-display text-xl font-bold text-primary">Services</h2>
              <p className="text-sm text-muted-foreground">Per-acre rates and minimum charge for each service.</p>
            </div>
            <Button onClick={addService} variant="outline" size="sm" data-testid="add-service-btn" className="rounded-full">
              <Plus className="h-4 w-4 mr-1.5" /> Add service
            </Button>
          </div>
          <div className="space-y-3">
            <div className="hidden md:grid md:grid-cols-12 gap-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground px-2">
              <div className="col-span-3">Key</div>
              <div className="col-span-4">Name</div>
              <div className="col-span-2">$/acre</div>
              <div className="col-span-2">Min charge</div>
              <div className="col-span-1"></div>
            </div>
            {cfg.services.map((s, i) => (
              <div key={i} data-testid={`service-row-${i}`} className="grid grid-cols-2 md:grid-cols-12 gap-3 items-center p-3 rounded-xl border border-border bg-background">
                <Input data-testid={`service-key-${i}`} className="col-span-1 md:col-span-3 h-10 rounded-lg" value={s.key} onChange={(e) => updateService(i, "key", e.target.value)} />
                <Input data-testid={`service-name-${i}`} className="col-span-1 md:col-span-4 h-10 rounded-lg" value={s.name} onChange={(e) => updateService(i, "name", e.target.value)} />
                <Input data-testid={`service-base-${i}`} type="number" step="0.5" min="0" className="col-span-1 md:col-span-2 h-10 rounded-lg" value={s.base_per_acre} onChange={(e) => updateService(i, "base_per_acre", e.target.value)} />
                <Input data-testid={`service-min-${i}`} type="number" step="10" min="0" className="col-span-1 md:col-span-2 h-10 rounded-lg" value={s.min_charge} onChange={(e) => updateService(i, "min_charge", e.target.value)} />
                <Button onClick={() => removeService(i)} variant="ghost" size="icon" data-testid={`service-del-${i}`} className="col-span-2 md:col-span-1 text-muted-foreground hover:text-destructive justify-self-end md:justify-self-center">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </section>

        {/* Crops */}
        <section className="bg-card border border-border rounded-3xl shadow-card p-6 md:p-8">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="font-display text-xl font-bold text-primary">Crops</h2>
              <p className="text-sm text-muted-foreground">Difficulty multiplier per crop type (1.00 = baseline, 1.20 = +20% harder).</p>
            </div>
            <Button onClick={addCrop} variant="outline" size="sm" data-testid="add-crop-btn" className="rounded-full">
              <Plus className="h-4 w-4 mr-1.5" /> Add crop
            </Button>
          </div>
          <div className="space-y-3">
            <div className="hidden md:grid md:grid-cols-12 gap-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground px-2">
              <div className="col-span-3">Key</div>
              <div className="col-span-6">Name</div>
              <div className="col-span-2">Multiplier</div>
              <div className="col-span-1"></div>
            </div>
            {cfg.crops.map((c, i) => (
              <div key={i} data-testid={`crop-row-${i}`} className="grid grid-cols-2 md:grid-cols-12 gap-3 items-center p-3 rounded-xl border border-border bg-background">
                <Input data-testid={`crop-key-${i}`} className="col-span-1 md:col-span-3 h-10 rounded-lg" value={c.key} onChange={(e) => updateCrop(i, "key", e.target.value)} />
                <Input data-testid={`crop-name-${i}`} className="col-span-1 md:col-span-6 h-10 rounded-lg" value={c.name} onChange={(e) => updateCrop(i, "name", e.target.value)} />
                <Input data-testid={`crop-mult-${i}`} type="number" step="0.05" min="0.1" max="5" className="col-span-1 md:col-span-2 h-10 rounded-lg" value={c.multiplier} onChange={(e) => updateCrop(i, "multiplier", e.target.value)} />
                <Button onClick={() => removeCrop(i)} variant="ghost" size="icon" data-testid={`crop-del-${i}`} className="col-span-2 md:col-span-1 text-muted-foreground hover:text-destructive justify-self-end md:justify-self-center">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

export { AdminNav };
